import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Calendar, FileText, Clock } from 'lucide-react';
import axios from 'axios';

const ManageExams = () => {
  const [exams, setExams] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [allSemesters, setAllSemesters] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    exam_type: 'Mid-term',
    course_id: '',
    semester_id: '',
    subject_id: '',
    start_date: '',
    end_date: '',
    exam_fee: 0,
    total_marks: 100,
    passing_marks: 40,
    is_active: true
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  const examTypes = ['Mid-term', 'Final', 'Unit Test', 'Practical', 'Assignment', 'Project'];

  useEffect(() => {
    fetchExams();
    fetchCourses();
    fetchSemesters();
    fetchSubjects();
  }, []);

  const fetchExams = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/examination/exams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExams(response.data);
    } catch (error) {
      console.error('Error fetching exams:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllSemesters(response.data);
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllSubjects(response.data);
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  // Filter semesters and subjects based on selected course
  const handleCourseChange = (courseId) => {
    setFormData({ ...formData, course_id: courseId, semester_id: '', subject_id: '' });
    
    if (courseId) {
      const filteredSemesters = allSemesters.filter(sem => sem.course_id === courseId);
      setSemesters(filteredSemesters);
      const filteredSubjects = allSubjects.filter(sub => sub.course_id === courseId);
      setSubjects(filteredSubjects);
    } else {
      setSemesters(allSemesters);
      setSubjects(allSubjects);
    }
  };

  // Filter subjects based on selected semester
  const handleSemesterChange = (semesterId) => {
    setFormData({ ...formData, semester_id: semesterId, subject_id: '' });
    
    if (semesterId) {
      const filteredSubjects = allSubjects.filter(sub => 
        sub.semester_id === semesterId && 
        (!formData.course_id || sub.course_id === formData.course_id)
      );
      setSubjects(filteredSubjects);
    } else if (formData.course_id) {
      const filteredSubjects = allSubjects.filter(sub => sub.course_id === formData.course_id);
      setSubjects(filteredSubjects);
    } else {
      setSubjects(allSubjects);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (formData.id) {
        await axios.put(
          `${API_URL}/api/examination/exams/${formData.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/api/examination/exams`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      alert('Exam saved successfully!');
      setShowModal(false);
      resetForm();
      fetchExams();
    } catch (error) {
      console.error('Error saving exam:', error);
      alert(error.response?.data?.detail || 'Failed to save exam.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this exam?')) return;
    try {
      await axios.delete(`${API_URL}/api/examination/exams/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Exam deleted successfully!');
      fetchExams();
    } catch (error) {
      console.error('Error deleting exam:', error);
      alert('Failed to delete exam.');
    }
  };

  const handleEdit = (exam) => {
    setFormData(exam);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      exam_type: 'Mid-term',
      course_id: '',
      semester_id: '',
      subject_id: '',
      start_date: '',
      end_date: '',
      exam_fee: 0,
      total_marks: 100,
      passing_marks: 40,
      is_active: true
    });
    setSemesters(allSemesters);
    setSubjects(allSubjects);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Exams</h1>
          <p className="text-sm text-gray-500 mt-1">Create and manage examination schedules</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add New Exam
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Exams</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{exams.length}</p>
            </div>
            <FileText className="h-12 w-12 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Exams</p>
              <p className="text-3xl font-bold text-green-600 mt-1">
                {exams.filter(e => e.is_active).length}
              </p>
            </div>
            <FileText className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">This Month</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">
                {exams.filter(e => {
                  const examDate = new Date(e.start_date);
                  const today = new Date();
                  return examDate.getMonth() === today.getMonth();
                }).length}
              </p>
            </div>
            <Calendar className="h-12 w-12 text-purple-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-3xl font-bold text-gray-600 mt-1">
                {exams.filter(e => new Date(e.end_date) < new Date()).length}
              </p>
            </div>
            <Clock className="h-12 w-12 text-gray-500" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exam Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Semester</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fee</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="9" className="px-6 py-4 text-center text-gray-500">Loading...</td>
              </tr>
            ) : exams.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-6 py-4 text-center text-gray-500">No exams found</td>
              </tr>
            ) : (
              exams.map((exam) => {
                const subject = allSubjects.find(s => s.id === exam.subject_id);
                return (
                  <tr key={exam.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{exam.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{exam.exam_type}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{exam.course_name || 'All'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{exam.semester_name || 'All'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {subject ? `${subject.name} (${subject.code})` : 'All Subjects'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {exam.start_date} to {exam.end_date}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">₹{exam.exam_fee || 0}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        exam.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {exam.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleEdit(exam)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(exam.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{formData.id ? 'Edit Exam' : 'Add New Exam'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Exam Name *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Exam Type *</label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.exam_type}
                    onChange={(e) => setFormData({ ...formData, exam_type: e.target.value })}
                  >
                    {examTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Course
                    {formData.course_id && (
                      <span className="text-xs text-blue-600 ml-2">
                        ({semesters.length} semesters)
                      </span>
                    )}
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.course_id}
                    onChange={(e) => handleCourseChange(e.target.value)}
                  >
                    <option value="">All Courses</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Semester
                    {!formData.course_id && (
                      <span className="text-xs text-gray-500 ml-2">(Select course first)</span>
                    )}
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.semester_id}
                    onChange={(e) => handleSemesterChange(e.target.value)}
                    disabled={!formData.course_id}
                  >
                    <option value="">
                      {formData.course_id ? 'All Semesters' : 'Select course first'}
                    </option>
                    {semesters.map(sem => (
                      <option key={sem.id} value={sem.id}>{sem.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject *
                    {!formData.course_id && (
                      <span className="text-xs text-gray-500 ml-2">(Select course first)</span>
                    )}
                    {formData.course_id && subjects.length > 0 && (
                      <span className="text-xs text-blue-600 ml-2">
                        ({subjects.length} subjects)
                      </span>
                    )}
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.subject_id}
                    onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
                    disabled={!formData.course_id}
                  >
                    <option value="">
                      {formData.course_id ? 'Select Subject' : 'Select course first'}
                    </option>
                    {subjects.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.name} ({sub.code})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                  <input
                    type="date"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Exam Fee (₹)</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.exam_fee}
                    onChange={(e) => setFormData({ ...formData, exam_fee: parseFloat(e.target.value) })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Marks</label>
                  <input
                    type="number"
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.total_marks}
                    onChange={(e) => setFormData({ ...formData, total_marks: parseInt(e.target.value) })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Passing Marks</label>
                  <input
                    type="number"
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    value={formData.passing_marks}
                    onChange={(e) => setFormData({ ...formData, passing_marks: parseInt(e.target.value) })}
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 mt-6">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Saving...' : 'Save Exam'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageExams;